import React from "react";
import { Modal, Button, Tag } from "antd";
import { OneFormulaData, CoreBrandData, NameCompletionRowData } from "./types";
import { NC_WEIGHT_KEYS } from "../../hooks/SettingsPage/nameCompletionHandlers";

const METRIC_LABELS: Record<string, string> = {
  marketPenetration: "Market Penetration",
  marketPenetrationOfClient: "Penetration of Client",
  avgCoreSkuClient: "Avg Core SKU",
  comboPresence: "Combo Presence",
  fixedComboPresence: "Fixed Combo",
  optionComboPresence: "Option Combo",
  nameAccuracy: "Name Accuracy",
  avgImagePresence: "Image Presence",
};

const METRIC_FIELDS = [
  "marketPenetration",
  "marketPenetrationOfClient",
  "avgCoreSkuClient",
  "comboPresence",
  "fixedComboPresence",
  "optionComboPresence",
  "nameAccuracy",
  "avgImagePresence",
] as const;

const CORE_BRAND_FIELDS = [
  "coreBrand1", "coreBrand2", "coreBrand3", "coreBrand4",
  "coreBrand5", "coreBrand6", "coreBrand7", "coreBrand8", "competitor",
] as const;

const NC_WEIGHT_LABELS: Record<string, string> = {
  subbrand_restaurant: "Restaurant — Subbrand",
  volume_restaurant: "Restaurant — Volume",
  packaging_type_restaurant: "Restaurant — Packaging Type",
  packaging_size_restaurant: "Restaurant — Packaging Size",
  subbrand_grocery: "Grocery — Subbrand",
  volume_grocery: "Grocery — Volume",
  packaging_type_grocery: "Grocery — Packaging Type",
  packaging_size_grocery: "Grocery — Packaging Size",
};

interface ReviewAllChangesModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  apiOneFormulaData: OneFormulaData[];
  editingOneFormulaData: { [key: string]: Partial<OneFormulaData> };
  displayCoreBrandsData: CoreBrandData[];
  editingCoreBrandsData: { [key: string]: Partial<CoreBrandData> };
  ncTableData: NameCompletionRowData[];
  ncEditingData: { [key: string]: Partial<NameCompletionRowData> };
  versionCount: number;
}

const parseBrandDisplayName = (value: string): string | null => {
  if (!value || value === "-") return null;
  try {
    const parsed = JSON.parse(value);
    return parsed.displayName || parsed.brandData?.name || null;
  } catch {
    return value;
  }
};

const parseCompetitorNames = (value: string): string[] => {
  if (!value || value === "-") return [];
  try {
    const parsed = JSON.parse(value);
    if (parsed.competitors && Array.isArray(parsed.competitors)) {
      return parsed.competitors.map((c: any) => {
        const name = c.company || c.competitor || "";
        return c.brand ? `${name} / ${c.brand}` : name;
      }).filter(Boolean);
    }
  } catch {
    return value.split(";").map((s: string) => s.trim()).filter(Boolean);
  }
  return [];
};

/** Semantic comparison for brand/competitor fields — compares parsed display values, not raw JSON */
const isBrandFieldChanged = (field: string, oldVal: string | undefined, newVal: string | undefined): boolean => {
  // Normalize undefined/null/empty to "-"
  const a = oldVal || "-";
  const b = newVal || "-";
  if (a === b) return false; // fast path: identical strings
  if (field === "competitor") {
    const oldNames = parseCompetitorNames(a).sort().join("|");
    const newNames = parseCompetitorNames(b).sort().join("|");
    return oldNames !== newNames;
  }
  const oldName = parseBrandDisplayName(a) || "-";
  const newName = parseBrandDisplayName(b) || "-";
  return oldName !== newName;
};

const ReviewAllChangesModal: React.FC<ReviewAllChangesModalProps> = ({
  open,
  onCancel,
  onConfirm,
  apiOneFormulaData,
  editingOneFormulaData,
  displayCoreBrandsData,
  editingCoreBrandsData,
  ncTableData,
  ncEditingData,
  versionCount,
}) => {
  const formulaChanges = apiOneFormulaData
    .filter(row => {
      const edits = editingOneFormulaData[row.key];
      if (!edits) return false;
      return METRIC_FIELDS.some(f => edits[f] !== undefined && edits[f] !== row[f]);
    })
    .map(row => ({
      market: row.market,
      changes: METRIC_FIELDS
        .filter(f => editingOneFormulaData[row.key]?.[f] !== undefined && editingOneFormulaData[row.key]?.[f] !== row[f])
        .map(f => ({
          label: METRIC_LABELS[f],
          old: row[f] as number,
          newVal: editingOneFormulaData[row.key]?.[f] as number,
        })),
    }));

  const coreBrandsChanges = displayCoreBrandsData
    .filter(row => {
      const edits = editingCoreBrandsData[row.key];
      if (!edits) return false;
      return CORE_BRAND_FIELDS.some(f => edits[f] !== undefined && isBrandFieldChanged(f, row[f] as string, edits[f] as string));
    })
    .map(row => ({
      market: row.market,
      changedSlots: CORE_BRAND_FIELDS
        .filter(f => editingCoreBrandsData[row.key]?.[f] !== undefined && isBrandFieldChanged(f, row[f] as string, editingCoreBrandsData[row.key]?.[f] as string))
        .map(f => f.startsWith("coreBrand") ? `Brand ${f.replace("coreBrand", "")}` : "Competitor"),
    }));

  const ncChanges = ncTableData
    .filter(row => {
      const edits = ncEditingData[row.key];
      if (!edits) return false;
      return NC_WEIGHT_KEYS.some(f => edits[f] !== undefined && edits[f] !== row[f]);
    })
    .map(row => ({
      market: row.key === "default-value" ? "Default Value" : row.market,
      changes: NC_WEIGHT_KEYS
        .filter(f => ncEditingData[row.key]?.[f] !== undefined && ncEditingData[row.key]?.[f] !== row[f])
        .map(f => ({
          label: NC_WEIGHT_LABELS[f],
          old: row[f] as number,
          newVal: ncEditingData[row.key]?.[f] as number,
        })),
    }));

  const hasNoChanges = formulaChanges.length === 0 && coreBrandsChanges.length === 0 && ncChanges.length === 0;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={<span style={{ fontSize: 15, fontWeight: 600, fontFamily: "Inter" }}>Review All Changes</span>}
      width={580}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <Button
            onClick={onCancel}
            style={{ borderRadius: 9999, fontFamily: "Inter" }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={onConfirm}
            disabled={hasNoChanges}
            style={{ borderRadius: 9999, fontFamily: "Inter", backgroundColor: hasNoChanges ? undefined : "#002882", borderColor: hasNoChanges ? undefined : "#002882" }}
          >
            Confirm & Run Pipeline
          </Button>
        </div>
      }
    >
      <div style={{ fontFamily: "Inter", display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
        {hasNoChanges ? (
          <div style={{ fontSize: 13, color: "#8c8c8c" }}>
            No changes detected. All three tables still contain original values.
          </div>
        ) : (
          <>
            {formulaChanges.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1f1f1f", marginBottom: 8 }}>
                  ONE Formula — {formulaChanges.length} {formulaChanges.length === 1 ? "market" : "markets"} changed
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {formulaChanges.map(({ market, changes }) => (
                    <div key={market} style={{ background: "#f9fafb", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{market}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {changes.map(({ label, old, newVal }) => (
                          <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6b7280" }}>
                            <span style={{ width: 160, flexShrink: 0 }}>{label}</span>
                            <span style={{ color: "#ef4444", textDecoration: "line-through" }}>{old}%</span>
                            <span style={{ color: "#9ca3af" }}>→</span>
                            <span style={{ color: "#16a34a", fontWeight: 500 }}>{newVal}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {coreBrandsChanges.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1f1f1f", marginBottom: 8 }}>
                  Core Brands — {coreBrandsChanges.length} {coreBrandsChanges.length === 1 ? "market" : "markets"} changed
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {coreBrandsChanges.map(({ market, changedSlots }) => (
                    <div key={market} style={{ background: "#f9fafb", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{market}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {changedSlots.map(slot => (
                          <Tag key={slot} color="blue" style={{ fontSize: 11, borderRadius: 999, margin: 0 }}>{slot}</Tag>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ncChanges.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1f1f1f", marginBottom: 8 }}>
                  Name Completion — {ncChanges.length} {ncChanges.length === 1 ? "market" : "markets"} changed
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {ncChanges.map(({ market, changes }) => (
                    <div key={market} style={{ background: "#f9fafb", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{market}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {changes.map(({ label, old, newVal }) => (
                          <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6b7280" }}>
                            <span style={{ width: 220, flexShrink: 0 }}>{label}</span>
                            <span style={{ color: "#ef4444", textDecoration: "line-through" }}>{old}%</span>
                            <span style={{ color: "#9ca3af" }}>→</span>
                            <span style={{ color: "#16a34a", fontWeight: 500 }}>{newVal}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 13, color: "rgba(0,0,0,0.9)", lineHeight: "20px" }}>
            <span>Please note: <br />The data refresh may take a few minutes to over an hour, depending on the size of the data. </span>
            <span style={{ fontWeight: 600 }}>No further configuration changes can be made while it is pending.</span>
            <span> You are free to leave this page — the update will continue in the background.</span>
          </div>
          <div style={{ fontSize: 13, color: "rgba(0,0,0,0.9)", lineHeight: "20px" }}>
            You will continue to see your current version of the data in all views until the update is complete.
          </div>
          <div style={{ padding: "8px 12px", borderRadius: 8, backgroundColor: "#EFF4FF", border: "1px solid #C7D9FF" }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#2B5BB8" }}>
              📧 You will receive an email notification when the data update is complete.
            </span>
          </div>
          {versionCount >= 3 && (
            <div style={{ padding: "8px 12px", borderRadius: 8, backgroundColor: "#FFF7E6", border: "1px solid #FFD591" }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: "#D46B08" }}>
                ⚠️ You have reached the maximum of 3 saved versions. The oldest version will be permanently removed when you save.
              </span>
            </div>
          )}
          <div style={{ fontSize: 13, color: "rgba(0,0,0,0.9)", lineHeight: "20px" }}>
            Are you sure you want to proceed?
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ReviewAllChangesModal;
